import { useCallback, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import {
  ActivityLogCareEnrollmentFragment,
  ActivityLogCareEnrollmentPaymentFragment,
  ActivityLogEnrollmentSupportEventFragment,
  CareBenefitActivityUsageFragment,
  CareSubscriptionPaymentStatus,
  EnrollmentStatus,
  ManageMembershipRequestType,
  SupportEventStatus,
  useGetActivityLogCareBenefitUsagesQuery,
  useGetActivityLogCarePlanEnrollmentsQuery,
  useGetActivityLogEnrollmentSupportEventsQuery,
  useSubscribeToActivityLogUpdatedSubscription,
} from 'shared/types/graphql';
import {
  ActivityLogManageMembershipRequests,
  ActivityLogModel,
  CareActivityLogItem,
  CareEnrollmentPaymentData,
  CareEnrollmentPaymentDataType,
  EnrollmentSupportEventData,
} from '../components/ActivityLog/types';
import {
  CareActivityLogFiltersOutput,
  CareActivityLogFilterType,
  useCareActivityLogFilters,
} from './useCareActivityLogFilters';

interface CareActivityLogInput {
  organizationPetId?: string;
  organizationPetParentId?: string;
  enrollmentId?: string;
}

interface CareActivityLogOutput extends CareActivityLogFiltersOutput {
  items: CareActivityLogItem[];
  loadingItems: boolean;
  fetchMore: () => Promise<void>;
  canShowMore: boolean;
}

const ITEMS_PER_PAGE = 10;

export const useCareActivityLog = ({
  organizationPetId,
  organizationPetParentId,
  enrollmentId,
}: CareActivityLogInput): CareActivityLogOutput => {
  const [take, setTake] = useState(ITEMS_PER_PAGE);
  const [totals, setTotals] = useState<Record<CareActivityLogFilterType, number>>({
    [CareActivityLogFilterType.EnrollmentEvent]: 0,
    [CareActivityLogFilterType.CareEnrollmentPayment]: 0,
    [CareActivityLogFilterType.BenefitUsage]: 0,
    [CareActivityLogFilterType.Note]: 0,
  });

  const buildEnrollmentEventsLogItems = useCallback(
    (data: ActivityLogCareEnrollmentFragment[]): CareActivityLogItem[] => {
      const events: CareActivityLogItem[] = [];
      data?.forEach((enrollment) => {
        const today = new Date();

        // Started or Renewed Event
        const startDate = new Date(enrollment.startDate);
        const isRenewal = !!enrollment.renewalPredecessorEnrollment;
        if (startDate < today) {
          events.push({
            date: startDate,
            data: {
              type: ActivityLogModel.CarePlanEnrollment,
              id: `${enrollment.id}-start`,
              currentPlanTitle: enrollment.plan.flowAlias || enrollment.plan.title,
              previousPlanTitle:
                enrollment.renewalPredecessorEnrollment?.plan.flowAlias ||
                enrollment.renewalPredecessorEnrollment?.plan.title,
              eventType: isRenewal ? 'MembershipRenewed' : 'MembershipStarted',
            },
          });
        }

        // Ended Event
        const endDate = !!enrollment.cancelledAt
          ? new Date(enrollment.cancelledAt)
          : new Date(enrollment.expirationDate);
        if (endDate < today && !enrollment.renewalSuccessorEnrollment) {
          events.push({
            date: endDate,
            data: {
              type: ActivityLogModel.CarePlanEnrollment,
              id: `${enrollment.id}-end`,
              renewalRejectionReason: enrollment.renewalRejectionReason,
              eventType: 'MembershipEnded',
            },
          });
        }

        // Renewal Email Event
        if (!!enrollment.renewalEmailNoticeSentAt) {
          const renewalNotificationDate = new Date(enrollment.renewalEmailNoticeSentAt);
          events.push({
            date: renewalNotificationDate,
            data: {
              type: ActivityLogModel.CarePlanEnrollment,
              id: `${enrollment.id}-renewal-email`,
              eventType: 'RenewalEmail',
              renewalDate: new Date(enrollment.expirationDate),
            },
          });
        }
      });

      return events;
    },
    [],
  );

  const buildEnrollmentSupportEventsLogItems = useCallback(
    (
      data: ActivityLogEnrollmentSupportEventFragment[],
      enrollments: ActivityLogCareEnrollmentFragment[],
    ): CareActivityLogItem[] => {
      const events: CareActivityLogItem[] =
        data?.map((supportEvent) => {
          const requestType = supportEvent.manageMembershipRequestType as ActivityLogManageMembershipRequests;
          let baseData: EnrollmentSupportEventData = {
            type: ActivityLogModel.EnrollmentSupportEvent,
            id: supportEvent.id,
            manageMembershipRequestType: requestType,
          };

          switch (requestType) {
            case ManageMembershipRequestType.Cancel:
              const isPayingAtClinic = !!supportEvent.carePlanEnrollment.careSubscriptionTrueUps.length;
              baseData = {
                ...baseData,
                amount: supportEvent.amount || 0,
                paymentSchedule: isPayingAtClinic ? 'PayInClinic' : supportEvent.paymentSchedule,
                cancelReason: supportEvent.cancelReason,
              };
              break;
            case ManageMembershipRequestType.Upgrade:
              baseData = {
                ...baseData,
                currentPlanTitle: supportEvent.currentPlan?.flowAlias || supportEvent.currentPlan?.title,
                newPlanTitle: supportEvent.newPlan?.flowAlias || supportEvent.newPlan?.title,
                amount: supportEvent.amount || 0,
              };
              break;
            case ManageMembershipRequestType.OptOut:
              const enrollmentId = supportEvent.carePlanEnrollmentId;
              const enrollment = enrollments?.find((enrollment) => enrollment.id === enrollmentId);
              if (!!enrollment) {
                baseData = { ...baseData, expirationDate: new Date(enrollment.expirationDate) };
              }
              break;
            case ManageMembershipRequestType.Transfer:
              baseData = {
                ...baseData,
                transferFromPetName: supportEvent.transferFromPet?.preferredName,
                transferToPetName: supportEvent.transferToPet?.preferredName,
              };
              break;
            case ManageMembershipRequestType.Note:
              baseData = {
                ...baseData,
                note: supportEvent.manageMembershipDescription || undefined,
              };
              break;
            default:
              break;
          }

          return {
            date: new Date(supportEvent.createdAt),
            user: supportEvent.requester,
            data: baseData,
          };
        }) || [];

      return events;
    },
    [],
  );

  const buildBenefitUsageEventsLogItems = useCallback(
    (data: CareBenefitActivityUsageFragment[]): CareActivityLogItem[] => {
      const events: CareActivityLogItem[] =
        data?.map((usage) => {
          return {
            date: new Date(usage.usedAt),
            user: usage.recordedByUser,
            data: {
              type: ActivityLogModel.CareBenefitUsage,
              id: usage.id,
              benefitTitle: usage.benefit.title,
              quantity: usage.quantityUsed,
              usageUnitOfMeasure: usage.benefit.usageUnitOfMeasure,
              amount: usage.invoiceLineItemConnections.reduce((acc, item) => acc + item.adjustedAmount, 0),
              usageClinic: {
                id: usage.usageClinic.id,
                name: usage.usageClinic.name,
              },
            },
          };
        }) || [];

      return events;
    },
    [],
  );

  const buildCareEnrollmentPaymentEventsLogItems = useCallback(
    (data: ActivityLogCareEnrollmentPaymentFragment[]): CareActivityLogItem[] => {
      const events: CareActivityLogItem[] = [];

      for (const payment of data) {
        const baseData = {
          type: ActivityLogModel.CareEnrollmentPayment,
          amount: payment.amount,
          paidAt: payment.paidAt && new Date(payment.paidAt),
          invoiceDate: payment.createdAt && new Date(payment.createdAt),
          lastPaymentAttempt: payment.lastPaymentAttempt && new Date(payment.lastPaymentAttempt),
          nextPaymentAttempt: payment.nextPaymentAttempt && new Date(payment.nextPaymentAttempt),
        } as CareEnrollmentPaymentData;

        if (payment.paidAt) {
          events.push({
            date: new Date(payment.paidAt),
            data: {
              ...baseData,
              subType: CareEnrollmentPaymentDataType.Payment,
              paymentMethod: payment.stripeInvoice?.stripePaymentMethod,
              status: CareSubscriptionPaymentStatus.Paid,
              id: `${payment.id}-paid`,
            },
          });
        }

        if (payment.stripeInvoice?.lapsedPayment) {
          events.push({
            date: new Date(payment.stripeInvoice?.lapsedPayment.createdAt),
            data: {
              ...baseData,
              subType: CareEnrollmentPaymentDataType.Payment,
              status: CareSubscriptionPaymentStatus.PastDue,
              id: `${payment.id}-past-due`,
            },
          });
        }

        if (payment.stripeDispute) {
          events.push({
            date: new Date(payment.stripeDispute.createdAt),
            data: {
              ...baseData,
              disputeDate: new Date(payment.stripeDispute.createdAt),
              subType: CareEnrollmentPaymentDataType.Dispute,
              status: payment.stripeDispute.status,
              id: `${payment.id}-disputed`,
            },
          });
        }

        if (payment.stripeRefund) {
          events.push({
            date: new Date(payment.stripeRefund.createdAt),
            data: {
              ...baseData,
              subType: CareEnrollmentPaymentDataType.Refund,
              refundDate: new Date(payment.stripeRefund.createdAt),
              amount: payment.stripeRefund.amount,
              id: `${payment.id}-refunded`,
            },
          });
        }
      }

      return events;
    },
    [],
  );

  const {
    loading: isLoadingCareBenefitUsages,
    data: careBenefitUsagesData,
    fetchMore: fetchMoreCareBenefitUsages,
    refetch: refetchCareBenefitUsages,
  } = useGetActivityLogCareBenefitUsagesQuery({
    variables: {
      benefitUsageWhere: {
        usageClinicPet: {
          petParents: { some: { organizationPetParentId: { equals: organizationPetParentId } } },
          organizationPetId: { equals: organizationPetId },
        },
      },
      take,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !organizationPetId || !organizationPetParentId,
  });

  const {
    loading: isLoadingCarePlanEnrollment,
    data: carePlanEnrollmentData,
    fetchMore: fetchMoreCarePlanEnrollment,
    refetch: refetchCarePlanEnrollment,
  } = useGetActivityLogCarePlanEnrollmentsQuery({
    variables: {
      enrollmentWhere: {
        organizationPetId: { equals: organizationPetId },
        status: {
          in: [EnrollmentStatus.Active, EnrollmentStatus.Expired],
        },
        enrollmentPayments: {
          some: {
            paidAt: {
              not: null,
            },
          },
        },
      },
      enrollmentPaymentWhere: {
        carePlanEnrollment: {
          organizationPetId: { equals: organizationPetId },
          status: {
            in: [EnrollmentStatus.Active, EnrollmentStatus.Expired],
          },
        },
      },
      take,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !organizationPetId,
  });

  const {
    loading: isLoadingEnrollmentSupportEvent,
    data: enrollmenSupportEventsData,
    refetch: refetchEnrollmentSupportEvent,
    fetchMore: fetchMoreEnrollmentSupportEvent,
  } = useGetActivityLogEnrollmentSupportEventsQuery({
    variables: {
      supportEventWhere: {
        carePlanEnrollment: {
          organizationPetId: {
            equals: organizationPetId,
          },
        },
        manageMembershipRequestType: {
          in: [
            ManageMembershipRequestType.Cancel,
            ManageMembershipRequestType.OptOut,
            ManageMembershipRequestType.Transfer,
            ManageMembershipRequestType.Upgrade,
            ManageMembershipRequestType.Note,
          ],
        },
        status: {
          equals: SupportEventStatus.Completed,
        },
      },
      supportEventCountWhere: {
        carePlanEnrollment: {
          organizationPetId: {
            equals: organizationPetId,
          },
        },
        manageMembershipRequestType: {
          in: [
            ManageMembershipRequestType.Cancel,
            ManageMembershipRequestType.OptOut,
            ManageMembershipRequestType.Transfer,
            ManageMembershipRequestType.Upgrade,
          ],
        },
        status: {
          equals: SupportEventStatus.Completed,
        },
      },
      noteEventCountWhere: {
        carePlanEnrollment: {
          organizationPetId: {
            equals: organizationPetId,
          },
        },
        manageMembershipRequestType: {
          equals: ManageMembershipRequestType.Note,
        },
        status: {
          equals: SupportEventStatus.Completed,
        },
      },
      take,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !organizationPetId,
  });

  useSubscribeToActivityLogUpdatedSubscription({
    variables: { enrollmentId: enrollmentId || '' },
    skip: !enrollmentId || !organizationPetId || !organizationPetParentId,
    onSubscriptionData: (data) => {
      const { model } = data.subscriptionData.data?.enrollmentActivityLogUpdated || {};
      switch (model) {
        case ActivityLogModel.CareBenefitUsage:
          refetchCareBenefitUsages();
          break;
        case ActivityLogModel.CarePlanEnrollment:
        case ActivityLogModel.CareEnrollmentPayment:
          refetchCarePlanEnrollment();
          break;
        case ActivityLogModel.EnrollmentSupportEvent:
          refetchEnrollmentSupportEvent();
          break;
        default:
          break;
      }
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const enrollmentEvents = useMemo(() => {
    const carePlanEnrollments = carePlanEnrollmentData?.findManyCarePlanEnrollment || [];
    return buildEnrollmentEventsLogItems(carePlanEnrollments);
  }, [buildEnrollmentEventsLogItems, carePlanEnrollmentData]);

  const isLoadingItems = isLoadingCareBenefitUsages || isLoadingCarePlanEnrollment || isLoadingEnrollmentSupportEvent;

  const sortedItems = useMemo(() => {
    // Items
    const carePlanEnrollments = carePlanEnrollmentData?.findManyCarePlanEnrollment || [];
    const enrollmentSupportEvents = enrollmenSupportEventsData?.findManyEnrollmentSupportEvent || [];
    const benefitUsages = careBenefitUsagesData?.findManyCareBenefitUsage || [];
    const careEnrollmentPayments = carePlanEnrollmentData?.findManyCareEnrollmentPayment || [];

    // Counters
    setTotals({
      [CareActivityLogFilterType.EnrollmentEvent]:
        enrollmentEvents.length + (enrollmenSupportEventsData?.findManyEnrollmentSupportEventCount || 0),
      [CareActivityLogFilterType.CareEnrollmentPayment]:
        carePlanEnrollmentData?.findManyCareEnrollmentPaymentCount || 0,
      [CareActivityLogFilterType.BenefitUsage]: careBenefitUsagesData?.findManyCareBenefitUsageCount || 0,
      [CareActivityLogFilterType.Note]: enrollmenSupportEventsData?.findManyEnrollmentSupportEventNotesCount || 0,
    });

    //build sorted items
    const events = [
      ...buildEnrollmentSupportEventsLogItems(enrollmentSupportEvents, carePlanEnrollments),
      ...buildBenefitUsageEventsLogItems(benefitUsages),
      ...buildCareEnrollmentPaymentEventsLogItems(careEnrollmentPayments),
      ...enrollmentEvents,
    ];
    return events.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [
    buildBenefitUsageEventsLogItems,
    buildCareEnrollmentPaymentEventsLogItems,
    buildEnrollmentSupportEventsLogItems,
    careBenefitUsagesData,
    carePlanEnrollmentData,
    enrollmenSupportEventsData,
    enrollmentEvents,
  ]);

  const careActivityLogFilters = useCareActivityLogFilters({ items: sortedItems, take });

  const { filteredItems, filteredItemsTotalCount } = careActivityLogFilters;

  const canFetchMore = useMemo(() => {
    return (
      careActivityLogFilters?.selectedFilters.some((filter) => {
        const totalForFilter = totals[filter];
        // For enrollment events, we need to account for manually built events
        if (filter === CareActivityLogFilterType.EnrollmentEvent) {
          return enrollmentEvents.length + take < totalForFilter;
        }
        // For other types, use the take value directly
        return take < totalForFilter;
      }) || false
    );
  }, [careActivityLogFilters?.selectedFilters, totals, take, enrollmentEvents]);

  const canShowMore = canFetchMore || filteredItems.length < filteredItemsTotalCount;

  const fetchMore = useCallback(async (): Promise<void> => {
    const newTake = take + ITEMS_PER_PAGE;
    setTake(newTake);
    if (!canFetchMore) return;
    await Promise.all([
      fetchMoreCareBenefitUsages({ variables: { take: newTake } }),
      fetchMoreCarePlanEnrollment({ variables: { take: newTake } }),
      fetchMoreEnrollmentSupportEvent({ variables: { take: newTake } }),
    ]);
  }, [take, canFetchMore, fetchMoreCareBenefitUsages, fetchMoreCarePlanEnrollment, fetchMoreEnrollmentSupportEvent]);

  return {
    items: sortedItems,
    loadingItems: isLoadingItems,
    fetchMore,
    canShowMore,
    ...careActivityLogFilters,
  };
};
